import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, ClipboardList, Copy, MessageCircle, Plus, Send, Star, X } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { bookingApi } from '../../api/bookingApi';
import { supportApi } from '../../api/supportApi';
import AdditionalTasksModal from '../../components/AdditionalTasksModal';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import PaymentModal from '../../components/PaymentModal';
import { connectGuestSocket, disconnectGuestSocket, getGuestSocket } from '../../socket/guestSocketClient';
import { clientPrice } from '../../utils/pricing';

const uid = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID?.()) ||
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const STATUS_META = {
  pending: { label: 'Waiting for response', className: 'bg-amber-50 text-amber-700 ring-amber-100' },
  accepted: { label: 'Accepted', className: 'bg-green-50 text-green-700 ring-green-100' },
  in_progress: { label: 'In progress', className: 'bg-blue-50 text-blue-700 ring-blue-100' },
  completed: { label: 'Completed', className: 'bg-green-50 text-green-700 ring-green-100' },
  cancelled: { label: 'Cancelled', className: 'bg-red-50 text-red-700 ring-red-100' },
  auto_cancelled: { label: 'Auto-cancelled', className: 'bg-red-50 text-red-700 ring-red-100' },
  declined: { label: 'Declined', className: 'bg-amber-50 text-amber-700 ring-amber-100' },
  lapsed: { label: 'No response', className: 'bg-amber-50 text-amber-700 ring-amber-100' },
};

const fmtDate = (date) =>
  date ? new Date(date).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' }) : '';
const fmtDeadline = (deadline) =>
  new Date(deadline).toLocaleString('en-AU', {
    weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true,
  });
const fmtDateTime = (date) =>
  date ? new Date(date).toLocaleString('en-AU', {
    weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true,
  }) : '';
const remaining = (deadline) => {
  const ms = new Date(deadline) - new Date();
  if (ms <= 0) return 'Any moment now';
  const mins = Math.floor(ms / 60000);
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  return hours >= 1 ? `${hours}h ${minutes}m left` : `${minutes}m left`;
};

const buildTimeline = (booking) => {
  const giggerName = booking.gigger?.name || 'your Gigger';
  const nodes = [{ key: 'requested', label: 'Requested', detail: `Booking sent to ${giggerName}`, state: 'done' }];

  switch (booking.status) {
    case 'pending':
      nodes.push({
        key: 'response',
        label: 'Awaiting response',
        state: 'current',
        chip: booking.responseDeadline ? remaining(booking.responseDeadline) : null,
        detail: booking.responseDeadline ? `${giggerName} has until ${fmtDeadline(booking.responseDeadline)} to accept` : `${giggerName} is reviewing your request`,
      });
      nodes.push({ key: 'progress', label: 'In progress', detail: 'Starts on the day, once accepted', state: 'upcoming' });
      nodes.push({ key: 'done', label: 'Completed', detail: 'Job finished', state: 'upcoming' });
      break;
    case 'accepted':
      nodes.push({ key: 'response', label: 'Accepted', detail: `${giggerName} accepted your booking`, state: 'current' });
      nodes.push({ key: 'progress', label: 'In progress', detail: `Starts ${fmtDate(booking.scheduledDate)}`, state: 'upcoming' });
      nodes.push({ key: 'done', label: 'Completed', detail: 'Job finished', state: 'upcoming' });
      break;
    case 'in_progress':
      nodes.push({ key: 'response', label: 'Accepted', detail: `${giggerName} accepted your booking`, state: 'done' });
      nodes.push({ key: 'progress', label: 'In progress', detail: 'Job underway', sub: fmtDateTime(booking.startConfirmedAt), state: 'current' });
      nodes.push({ key: 'done', label: 'Completed', detail: 'Job finished', state: 'upcoming' });
      break;
    case 'completed':
      nodes.push({ key: 'response', label: 'Accepted', detail: `${giggerName} accepted your booking`, state: 'done' });
      nodes.push({ key: 'progress', label: 'Started', detail: 'Job underway', sub: fmtDateTime(booking.startConfirmedAt), state: 'done' });
      nodes.push({ key: 'done', label: 'Completed', state: 'current', detail: booking.review?.rating ? 'Reviewed — thanks!' : 'Job finished', sub: fmtDateTime(booking.endConfirmedAt) });
      break;
    case 'lapsed':
      nodes.push({ key: 'response', label: 'No response', detail: `${giggerName} didn't respond in time`, state: 'warn' });
      nodes.push({ key: 'choice', label: 'Waiting on you', detail: 'Keep waiting, pick another Gigger, or cancel', state: 'current' });
      break;
    case 'declined':
      nodes.push({ key: 'response', label: 'Declined', detail: `${giggerName} can't take this booking`, state: 'warn' });
      nodes.push({ key: 'choice', label: 'Waiting on you', detail: 'Pick another Gigger or cancel', state: 'current' });
      break;
    case 'cancelled':
    case 'auto_cancelled': {
      if (booking.startConfirmedAt) {
        nodes.push({ key: 'response', label: 'Accepted', detail: `${giggerName} accepted your booking`, state: 'done' });
        nodes.push({ key: 'progress', label: 'Started', detail: 'Job underway', sub: fmtDateTime(booking.startConfirmedAt), state: 'done' });
      }
      const cancellation = booking.cancellation || {};
      const by = cancellation.by === 'client' ? 'you' : cancellation.by === 'gigger' ? giggerName : cancellation.by === 'admin' ? 'an admin' : null;
      const reasonMap = {
        gigger_no_show: `${giggerName} didn't show up`,
        client_unavailable: 'you were no longer available',
        gigger_cancelled: `${giggerName} cancelled the job`,
        other: cancellation.detail || 'other reason',
      };
      nodes.push({
        key: 'cancelled',
        label: booking.status === 'auto_cancelled' ? 'Auto-cancelled — no response' : `Cancelled${by ? ` by ${by}` : ''}`,
        detail: cancellation.reason ? (reasonMap[cancellation.reason] || cancellation.detail || '') : (cancellation.detail || ''),
        sub: fmtDateTime(cancellation.at),
        state: 'fail',
      });
      break;
    }
    default:
      break;
  }
  return nodes;
};

const TimelineDot = ({ state }) => {
  const className = {
    done: 'bg-green-700 text-white',
    current: 'bg-blue-600 text-white',
    warn: 'bg-amber-500 text-white',
    fail: 'bg-red-600 text-white',
    upcoming: 'border border-slate-200 bg-white text-slate-300',
  }[state] || 'border border-slate-200 bg-white text-slate-300';

  return (
    <span className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${className}`}>
      {state === 'done' && <Check size={14} strokeWidth={3} />}
      {state === 'current' && <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-white" />}
      {state === 'warn' && <AlertTriangle size={14} />}
      {state === 'fail' && <X size={14} strokeWidth={3} />}
      {state === 'upcoming' && <span className="h-2.5 w-2.5 rounded-full border border-slate-300" />}
    </span>
  );
};

const mapRaw = (message) => ({
  _id: String(message._id),
  from: message.fromGuest ? 'me' : 'them',
  text: message.content,
  at: message.createdAt,
  clientMessageId: message.clientMessageId || null,
  senderName: message.fromGuest ? 'You' : message.sender?.name || 'Gigger',
});
const mapSerialized = (message) => ({
  _id: String(message._id),
  from: message.from === 'me' ? 'me' : 'them',
  text: message.text,
  at: message.at,
  senderName: message.senderName,
});

const ReassignChoices = ({ booking, busy, act }) => {
  const isLapsed = booking.status === 'lapsed';
  const [pickerOpen, setPickerOpen] = useState(false);
  const [giggers, setGiggers] = useState(null);
  const [loadingGiggers, setLoadingGiggers] = useState(false);
  const [giggerError, setGiggerError] = useState('');

  const openPicker = async () => {
    setPickerOpen(true);
    if (giggers) return;
    setLoadingGiggers(true);
    setGiggerError('');
    try {
      const response = await bookingApi.availableGiggers(booking.trackingId);
      setGiggers(response.data || []);
    } catch (err) {
      setGiggerError(err.response?.data?.message || "Couldn't load available Giggers.");
    } finally {
      setLoadingGiggers(false);
    }
  };

  return (
    <div className="min-w-0 rounded-3xl border border-amber-200 bg-amber-50 p-4 shadow-sm sm:p-6">
      <h2 className="m-0 break-words text-lg font-black text-amber-950">
        {isLapsed ? `${booking.gigger?.name} didn't respond in time` : `${booking.gigger?.name} declined this booking`}
      </h2>
      <p className="mb-0 mt-2 break-words text-sm leading-6 text-amber-900/80">
        {isLapsed
          ? 'You can keep waiting, pick another Gigger, or cancel. No charge either way.'
          : 'You can pick another Gigger or cancel. No charge either way.'}
      </p>

      {!pickerOpen ? (
        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          {isLapsed && (
            <button disabled={busy === 'keep'} onClick={() => act('keep', () => bookingApi.keepWaiting(booking.trackingId))} className="cursor-pointer rounded-xl border-0 bg-green-700 px-4 py-3 text-sm font-bold text-white disabled:opacity-50">Keep waiting</button>
          )}
          <button type="button" onClick={openPicker} className="cursor-pointer rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-800">Choose another Gigger</button>
          <button disabled={busy === 'drop'} onClick={() => act('drop', () => bookingApi.cancelUnfilled(booking.trackingId))} className="cursor-pointer border-0 bg-transparent px-3 py-3 text-sm font-bold text-red-700 disabled:opacity-50">Cancel booking</button>
        </div>
      ) : (
        <div className="mt-5 min-w-0">
          {loadingGiggers ? (
            <div className="py-7 text-center text-sm text-slate-500">Finding available Giggers...</div>
          ) : giggerError ? (
            <div className="break-words rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">{giggerError}</div>
          ) : giggers && giggers.length === 0 ? (
            <div className="rounded-xl bg-white p-4 text-center text-sm text-slate-500">No other Giggers are available for this service, date and time.</div>
          ) : (
            <div className="space-y-3">
              {(giggers || []).map((gigger) => (
                <div key={gigger._id} className="flex min-w-0 flex-col gap-3 rounded-2xl bg-white p-4 sm:flex-row sm:items-center">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-green-700 text-sm font-black text-white">
                    {gigger.avatar ? <img src={gigger.avatar} alt={gigger.name} className="h-full w-full object-cover" /> : (gigger.name || '?').charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="break-words text-sm font-bold text-slate-950">{gigger.name}</div>
                    <div className="mt-1 break-words text-xs text-slate-500">{gigger.ratingAvg ? `★ ${gigger.ratingAvg} · ` : ''}{gigger.distanceKm != null ? `${gigger.distanceKm} km away` : gigger.suburb || ''}</div>
                  </div>
                  {gigger.hourlyRate != null && <div className="shrink-0 text-sm font-black text-slate-950">${clientPrice(gigger.hourlyRate)}<span className="ml-1 text-[11px] font-medium text-slate-400">/hr</span></div>}
                  <button disabled={busy === `re:${gigger._id}`} onClick={() => act(`re:${gigger._id}`, () => bookingApi.reassign(booking.trackingId, gigger._id))} className="cursor-pointer rounded-xl border-0 bg-green-700 px-4 py-2.5 text-xs font-bold text-white disabled:opacity-50">Choose</button>
                </div>
              ))}
            </div>
          )}
          <button type="button" onClick={() => setPickerOpen(false)} className="mt-3 cursor-pointer border-0 bg-transparent p-0 text-sm font-bold text-slate-500">Back</button>
        </div>
      )}
    </div>
  );
};

const Track = () => {
  const { trackingId } = useParams();
  const navigate = useNavigate();

  const [idInput, setIdInput] = useState('');
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(!!trackingId);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [copied, setCopied] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('client_unavailable');
  const [cancelDetail, setCancelDetail] = useState('');
  const [showTasks, setShowTasks] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [payModal, setPayModal] = useState(null);
  const [thread, setThread] = useState([]);
  const [draft, setDraft] = useState('');
  const scrollRef = useRef(null);
  const chatRef = useRef(null);
  const [, forceTick] = useState(0);

  const load = useCallback(async () => {
    if (!trackingId) return;
    try {
      const response = await bookingApi.track(trackingId);
      setBooking(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'No booking found for that tracking ID.');
    } finally {
      setLoading(false);
    }
  }, [trackingId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!booking || booking.status !== 'pending') return undefined;
    const timer = setInterval(() => forceTick((value) => value + 1), 30000);
    return () => clearInterval(timer);
  }, [booking]);

  useEffect(() => {
    const chat = booking?.chat;
    if (!chat?.guestToken || chatRef.current) return undefined;
    chatRef.current = chat;
    const socket = connectGuestSocket(chat.guestToken);

    const onMessage = (message) => setThread((current) => {
      const incomingId = String(message._id);
      if (current.some((item) => item._id && String(item._id) === incomingId)) return current;
      const mapped = mapRaw(message);
      if (message.clientMessageId && current.some((item) => item.clientMessageId === message.clientMessageId)) {
        return current.map((item) => item.clientMessageId === message.clientMessageId ? { ...mapped, clientMessageId: message.clientMessageId } : item);
      }
      return [...current, mapped];
    });
    const onBookingUpdate = () => load();

    socket.on('message:new', onMessage);
    socket.on('booking:updated', onBookingUpdate);

    supportApi
      .getMessages(chat.conversationId, chat.guestToken)
      .then((response) => setThread((response.data?.data || []).map(mapSerialized)))
      .catch(() => {});

    return () => {
      socket.off('message:new', onMessage);
      socket.off('booking:updated', onBookingUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.chat?.conversationId]);

  useEffect(() => () => disconnectGuestSocket(), []);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread]);

  const act = async (key, fn) => {
    setBusy(key);
    setError('');
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong.');
    } finally {
      setBusy('');
    }
  };

  const sendChat = (event) => {
    event?.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    const clientMessageId = uid();
    setThread((current) => [...current, { _id: null, from: 'me', text, at: new Date(), clientMessageId, pending: true }]);
    const socket = getGuestSocket();

    if (socket?.connected) {
      socket.emit('support:guest:send', { content: text, clientMessageId }, (ack) => {
        if (ack?.ok && ack.raw) {
          setThread((current) => current.map((message) => message.clientMessageId === clientMessageId ? mapRaw(ack.raw) : message));
        }
      });
    } else {
      supportApi
        .sendMessage(chatRef.current.conversationId, chatRef.current.guestToken, { content: text, clientMessageId })
        .then((response) => response.data?.data && setThread((current) => current.map((message) => message.clientMessageId === clientMessageId ? mapSerialized(response.data.data) : message)))
        .catch(() => {});
    }
  };

  const copyId = () => {
    navigator.clipboard?.writeText(trackingId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const inputClass =
    'box-border w-full rounded-2xl border-0 bg-slate-100/80 px-4 py-3.5 text-[15px] text-slate-950 outline-none ring-1 ring-inset ring-slate-200 transition-all duration-200 placeholder:text-slate-400 hover:bg-white hover:ring-slate-300 focus:bg-white focus:ring-2 focus:ring-green-500 focus:shadow-[0_8px_24px_rgba(22,101,52,0.10)]';
  const cardClass = 'min-w-0 rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_10px_30px_rgba(15,23,42,0.06)] sm:p-6';

  if (!trackingId) {
    return (
      <>
        <Header />
        <title>Track Your Gig | GIGGS</title>
        <main className="flex min-h-[65vh] items-center justify-center overflow-x-hidden bg-slate-50 px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
          <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-5 text-center shadow-[0_14px_40px_rgba(15,23,42,0.08)] sm:p-8">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-green-50 text-green-700"><ClipboardList size={24} /></span>
            <h1 className="mb-0 mt-5 text-[clamp(1.875rem,6vw,2.25rem)] font-black leading-tight text-slate-950">Track your Gig</h1>
            <p className="mb-0 mt-2 text-sm leading-6 text-slate-600">Enter the tracking ID from your booking confirmation.</p>
            <input className={`${inputClass} mt-6 text-center font-mono font-bold uppercase`} value={idInput} onChange={(event) => setIdInput(event.target.value.toUpperCase())} placeholder="GG-XXXXX" onKeyDown={(event) => event.key === 'Enter' && idInput.trim() && navigate(`/track/${idInput.trim()}`)} />
            <button type="button" onClick={() => idInput.trim() && navigate(`/track/${idInput.trim()}`)} disabled={!idInput.trim()} className="mt-3 w-full cursor-pointer rounded-2xl border-0 bg-green-700 px-5 py-3.5 text-sm font-bold text-white transition hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-40">Track booking</button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (loading || (!booking && !error)) {
    return <><Header /><main className="flex min-h-[60vh] items-center justify-center bg-slate-50 px-4 text-center text-sm font-semibold text-slate-500">Loading booking...</main><Footer /></>;
  }

  if (error && !booking) {
    return (
      <>
        <Header />
        <main className="flex min-h-[60vh] items-center justify-center overflow-x-hidden bg-slate-50 px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
          <div className="w-full max-w-md text-center">
            <AlertTriangle size={38} className="mx-auto text-red-600" />
            <h1 className="mb-0 mt-4 text-2xl font-black text-slate-950">Booking not found</h1>
            <p className="mb-0 mt-2 break-words text-sm leading-6 text-red-600">{error}</p>
            <button type="button" onClick={() => navigate('/track')} className="mt-6 cursor-pointer rounded-xl border-0 bg-green-700 px-5 py-3 text-sm font-bold text-white">Try another ID</button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const b = booking;
  const giggerName = b.gigger?.name || 'your Gigger';
  const meta = STATUS_META[b.status] || STATUS_META.pending;
  const timeline = buildTimeline(b);
  const canCancel = ['pending', 'accepted', 'in_progress'].includes(b.status);
  const canAddTasks = ['accepted', 'in_progress'].includes(b.status);
  const contactRevealed = ['accepted', 'in_progress', 'completed'].includes(b.status);
  const tasks = b.extraHoursRequests || [];
  const clientRate = clientPrice(b.pricing?.hourlyRate || 0);
  const baseHours = b.pricing?.hours || 0;
  const extraHours = b.pricing?.extraHours || 0;
  const baseCost = clientRate * baseHours;
  const extraCost = clientRate * extraHours;
  const payment = b.payment || {};
  const clientTotal = payment.clientTotal != null ? payment.clientTotal : clientRate * (baseHours + extraHours);
  const depositAmount = payment.depositAmount != null ? payment.depositAmount : clientTotal * 0.2;
  const balanceAmount = payment.balanceAmount != null ? payment.balanceAmount : clientTotal - clientTotal * 0.2;
  const depositPaid = payment.depositStatus === 'paid';
  const balancePaid = payment.balanceStatus === 'paid';
  const needsDeposit = b.status === 'accepted' && !depositPaid;
  const needsBalance = b.status === 'completed' && depositPaid && !balancePaid;
  const showPaymentBreakdown = ['accepted', 'in_progress', 'completed'].includes(b.status);

  const TaskRow = ({ task }) => {
    const badge = task.status === 'accepted' ? 'Added' : task.status === 'declined' ? 'Declined' : `Awaiting ${giggerName}`;
    const badgeClass = task.status === 'accepted' ? 'bg-green-50 text-green-700' : task.status === 'declined' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700';
    const cost = (task.hours || 0) * clientRate;
    return (
      <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
          <div className="min-w-0">
            <div className="break-words text-sm font-bold text-slate-950">{task.taskName || task.description}</div>
            {task.details && <div className="mt-1 break-words text-xs leading-5 text-slate-500">{task.details}</div>}
          </div>
          <span className={`w-fit shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ${badgeClass}`}>{badge}</span>
        </div>
        <div className="mt-2 flex min-w-0 flex-col gap-2 text-xs text-slate-500 sm:flex-row sm:items-end sm:justify-between sm:gap-3">
          <span className="min-w-0 break-words">{task.status === 'accepted' ? `+${task.hours} hr added to the job` : task.status === 'declined' ? 'Not taken on — book it as a separate job' : `Expected time: ${task.hours || 0.5} hr`}</span>
          {task.status === 'accepted' && <span className="shrink-0 font-black text-green-700">+${cost.toFixed(2)}</span>}
        </div>
      </div>
    );
  };

  return (
    <>
      <Header />
      <title>Booking {b.trackingId} | GIGGS</title>

      <main className="min-h-[70vh] overflow-x-hidden bg-slate-50 py-8 text-slate-900 sm:py-10 lg:py-12">
        <div className="mx-auto w-full max-w-[1500px] px-4 sm:px-6 lg:px-8 2xl:px-10">
          <div className="mb-5 flex min-w-0 flex-col gap-4 rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_10px_30px_rgba(15,23,42,0.06)] sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div className="min-w-0">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Booking</span>
              <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2">
                <span className="min-w-0 break-all font-mono text-lg font-black text-slate-950 sm:text-xl">{b.trackingId}</span>
                <button type="button" onClick={copyId} className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-600"><Copy size={13} /> {copied ? 'Copied' : 'Copy'}</button>
              </div>
            </div>
            <span className={`w-fit shrink-0 rounded-full px-3 py-1.5 text-xs font-black ring-1 ${meta.className}`}>{meta.label}</span>
          </div>

          {error && <div className="mb-5 break-words rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</div>}

          <div className={`grid min-w-0 gap-5 ${b.chat ? 'xl:grid-cols-[minmax(0,0.82fr)_minmax(0,1.25fr)_minmax(0,0.93fr)]' : 'lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]'}`}>
            <aside className="min-w-0 space-y-5">
              <section className={cardClass}>
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-green-700 text-sm font-black text-white">
                    {b.gigger?.avatar ? <img src={b.gigger.avatar} alt={giggerName} className="h-full w-full object-cover" /> : giggerName.charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="break-words text-sm font-black text-slate-950">{b.gigger?.name}</div>
                    <div className="mt-1 break-words text-xs text-slate-500">
                      {(b.gigger?.reviewCount > 0 || b.gigger?.completedJobs > 0) && <span>★ {b.gigger.ratingAvg} ({b.gigger.reviewCount}){b.gigger.completedJobs > 0 ? ` · ${b.gigger.completedJobs} jobs` : ''} · </span>}
                      {b.gigger?._id && <Link to={`/gigger/${b.gigger._id}`} target="_blank" className="font-bold text-green-700 no-underline">View profile</Link>}
                    </div>
                  </div>
                </div>

                <dl className="mt-5 divide-y divide-slate-100">
                  {[
                    ['Service', b.service?.name],
                    ['Date', fmtDate(b.scheduledDate)],
                    ['Time', `${b.startTime} – ${b.endTime}`],
                    ...(contactRevealed && b.gigger?.phone ? [['Gigger contact', b.gigger.phone]] : []),
                  ].map(([label, value]) => (
                    <div key={label} className="flex min-w-0 items-start justify-between gap-4 py-3 first:pt-0 last:pb-0">
                      <dt className="shrink-0 text-xs font-semibold text-slate-400">{label}</dt>
                      <dd className="m-0 min-w-0 break-words text-right text-sm font-bold text-slate-800">{value}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              <section className={cardClass}>
                <h2 className="m-0 text-base font-black text-slate-950">Cost</h2>
                <div className="mt-4 space-y-3 text-sm">
                  <div className="flex min-w-0 justify-between gap-4"><span className="min-w-0 break-words text-slate-500">${clientRate}/hr × {baseHours} hr</span><span className="shrink-0 font-bold text-slate-800">${baseCost.toFixed(2)}</span></div>
                  {extraHours > 0 && <div className="flex min-w-0 justify-between gap-4 text-green-700"><span className="min-w-0 break-words">Additional tasks ({extraHours} hr)</span><span className="shrink-0 font-bold">+${extraCost.toFixed(2)}</span></div>}
                  <div className="h-px bg-slate-100" />
                  <div className="flex justify-between gap-4"><span className="font-bold text-slate-950">Total</span><span className="shrink-0 text-lg font-black text-slate-950">${clientTotal.toFixed(2)}</span></div>
                  {showPaymentBreakdown && (
                    <div className="space-y-2 rounded-xl bg-slate-50 p-3 text-xs text-slate-500">
                      <div className="flex min-w-0 justify-between gap-3"><span className="min-w-0 break-words">Deposit {depositPaid ? '(paid)' : '(due)'}</span><span className="shrink-0 font-bold text-slate-700">${depositAmount.toFixed(2)}</span></div>
                      <div className="flex min-w-0 justify-between gap-3"><span className="min-w-0 break-words">Balance {balancePaid ? '(paid)' : '(due at completion)'}</span><span className="shrink-0 font-bold text-slate-700">${balanceAmount.toFixed(2)}</span></div>
                    </div>
                  )}
                </div>
              </section>
            </aside>

            <section className="min-w-0 space-y-5">
              <section className={cardClass}>
                <h2 className="m-0 text-base font-black text-slate-950">Progress</h2>
                <ol className="mt-5 list-none p-0">
                  {timeline.map((node, index) => (
                    <li key={node.key} className="relative flex min-w-0 gap-4 pb-6 last:pb-0">
                      {index < timeline.length - 1 && <span className="absolute left-[15px] top-8 bottom-0 w-px bg-slate-200" />}
                      <TimelineDot state={node.state} />
                      <div className="min-w-0 flex-1 pt-1">
                        <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                          <span className="min-w-0 break-words text-sm font-bold text-slate-950">{node.label}</span>
                          {node.chip && <span className="shrink-0 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-bold text-amber-700">{node.chip}</span>}
                        </div>
                        {node.detail && <div className="mt-1 break-words text-xs leading-5 text-slate-500">{node.detail}</div>}
                        {node.sub && <div className="mt-1 break-words text-[11px] text-slate-400">{node.sub}</div>}
                      </div>
                    </li>
                  ))}
                </ol>
              </section>

              {needsDeposit && (
                <section className="min-w-0 rounded-3xl border border-green-200 bg-green-50 p-4 shadow-sm sm:p-6">
                  <h2 className="m-0 break-words text-lg font-black text-green-950">Pay your deposit to confirm</h2>
                  <p className="mb-0 mt-2 break-words text-sm leading-6 text-green-900/80">{giggerName} accepted your booking. Pay the 20% deposit (${depositAmount.toFixed(2)}) to confirm. The remaining ${balanceAmount.toFixed(2)} is due when the job is complete.</p>
                  <button type="button" onClick={() => setPayModal('deposit')} className="mt-5 w-full cursor-pointer rounded-xl border-0 bg-green-700 px-5 py-3.5 text-sm font-bold text-white">Pay ${depositAmount.toFixed(2)} deposit</button>
                </section>
              )}

              {b.status === 'accepted' && depositPaid && (
                <section className={cardClass}>
                  <div className="flex items-start gap-2 text-sm font-bold text-green-700"><Check size={18} className="mt-0.5 shrink-0" /> <span className="min-w-0 break-words">Deposit paid — you're confirmed</span></div>
                  <p className="mb-0 mt-2 break-words text-xs leading-5 text-slate-500">{giggerName} will start on the scheduled day. The remaining ${balanceAmount.toFixed(2)} is due once the job is complete.</p>
                </section>
              )}

              {needsBalance && (
                <section className="min-w-0 rounded-3xl border border-green-200 bg-green-50 p-4 shadow-sm sm:p-6">
                  <h2 className="m-0 break-words text-lg font-black text-green-950">Pay your balance</h2>
                  <p className="mb-0 mt-2 break-words text-sm leading-6 text-green-900/80">{giggerName} completed the job. Pay the remaining balance of ${balanceAmount.toFixed(2)} to finish up.</p>
                  <button type="button" onClick={() => setPayModal('balance')} className="mt-5 w-full cursor-pointer rounded-xl border-0 bg-green-700 px-5 py-3.5 text-sm font-bold text-white">Pay ${balanceAmount.toFixed(2)} balance</button>
                </section>
              )}

              {b.status === 'completed' && balancePaid && <section className={cardClass}><div className="flex items-start gap-2 text-sm font-bold text-green-700"><Check size={18} className="mt-0.5 shrink-0" /> <span>Paid in full — thank you</span></div></section>}

              {['lapsed', 'declined'].includes(b.status) && <ReassignChoices booking={b} busy={busy} act={act} />}

              {(canAddTasks || tasks.length > 0) && (
                <section className={cardClass}>
                  <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                    <div className="flex min-w-0 items-center gap-2">
                      <ClipboardList size={18} className="shrink-0 text-green-700" />
                      <h2 className="m-0 min-w-0 break-words text-base font-black text-slate-950">Additional tasks</h2>
                      {tasks.length > 0 && <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600">{tasks.length}</span>}
                    </div>
                    <button type="button" onClick={() => setShowTasks(true)} className="inline-flex w-fit shrink-0 cursor-pointer items-center gap-1.5 rounded-xl border-0 bg-green-700 px-3 py-2.5 text-xs font-bold text-white">{canAddTasks ? <><Plus size={14} /> Request task</> : 'View tasks'}</button>
                  </div>

                  {tasks.length === 0 ? (
                    <p className="mb-0 mt-4 break-words rounded-xl bg-slate-50 p-4 text-xs leading-5 text-slate-500">Need something extra done during the job? Choose it from the service task list. Expected time is set by Giggs before {giggerName} accepts it.</p>
                  ) : (
                    <div className="mt-4 space-y-3">
                      {tasks.slice(-2).map((task) => <TaskRow task={task} key={task._id} />)}
                      {tasks.length > 2 && <button type="button" onClick={() => setShowTasks(true)} className="cursor-pointer border-0 bg-transparent p-0 text-sm font-bold text-green-700">View all {tasks.length} tasks</button>}
                    </div>
                  )}
                </section>
              )}

              {b.canReview && (
                <section className={cardClass}>
                  <h2 className="m-0 break-words text-lg font-black text-slate-950">How did {giggerName} do?</h2>
                  <div className="mt-4 flex flex-wrap gap-1">
                    {[1, 2, 3, 4, 5].map((number) => (
                      <button key={number} type="button" onClick={() => setRating(number)} className="cursor-pointer border-0 bg-transparent p-1" aria-label={`${number} stars`}>
                        <Star size={28} fill={number <= rating ? 'currentColor' : 'none'} className={number <= rating ? 'text-amber-500' : 'text-slate-300'} />
                      </button>
                    ))}
                  </div>
                  <textarea className={`${inputClass} mt-3 min-h-24 resize-y`} value={comment} onChange={(event) => setComment(event.target.value)} rows={3} placeholder="Share a few words (optional)..." />
                  <button disabled={busy === 'review' || rating === 0} onClick={() => act('review', () => bookingApi.review(b.trackingId, { rating, comment }))} className="mt-3 w-full cursor-pointer rounded-xl border-0 bg-green-700 px-5 py-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto">Submit review</button>
                </section>
              )}

              {b.status === 'completed' && b.review?.rating && (
                <section className={cardClass}>
                  <div className="flex items-start gap-2 text-sm font-bold text-green-700"><Check size={18} className="mt-0.5 shrink-0" /> <span className="min-w-0 break-words">Thanks for reviewing {giggerName}</span></div>
                  <div className="mt-3 flex flex-wrap gap-1">{[1, 2, 3, 4, 5].map((number) => <Star key={number} size={18} fill={number <= b.review.rating ? 'currentColor' : 'none'} className={number <= b.review.rating ? 'text-amber-500' : 'text-slate-300'} />)}</div>
                  {b.review.comment && <p className="mb-0 mt-3 break-words text-sm leading-6 text-slate-600">“{b.review.comment}”</p>}
                </section>
              )}

              {canCancel && (
                <div className="min-w-0">
                  {!showCancel ? (
                    <button type="button" onClick={() => setShowCancel(true)} className="cursor-pointer border-0 bg-transparent p-0 text-sm font-bold text-red-700">Cancel this booking</button>
                  ) : (
                    <section className="min-w-0 rounded-3xl border border-red-200 bg-red-50 p-4 sm:p-6">
                      <div className="flex min-w-0 items-center justify-between gap-4">
                        <h2 className="m-0 min-w-0 break-words text-base font-black text-red-950">Cancel booking</h2>
                        <button type="button" onClick={() => setShowCancel(false)} className="shrink-0 cursor-pointer border-0 bg-transparent p-1 text-slate-500"><X size={18} /></button>
                      </div>
                      <div className="mt-4 space-y-3">
                        {[
                          ...(['accepted', 'in_progress'].includes(b.status) ? [{ v: 'gigger_no_show', label: "The Gigger didn't show up" }] : []),
                          { v: 'client_unavailable', label: "I'm no longer available" },
                          { v: 'other', label: 'Other reason' },
                        ].map((option) => (
                          <label key={option.v} className="flex min-w-0 cursor-pointer items-start gap-3 text-sm text-red-950">
                            <input className="mt-1 shrink-0" type="radio" name="cancel" checked={cancelReason === option.v} onChange={() => setCancelReason(option.v)} />
                            <span className="min-w-0 break-words">{option.label}</span>
                          </label>
                        ))}
                      </div>
                      {cancelReason === 'other' && <textarea className={`${inputClass} mt-4 min-h-20 resize-y`} value={cancelDetail} onChange={(event) => setCancelDetail(event.target.value)} rows={2} placeholder="Tell us what happened..." />}
                      <button disabled={busy === 'cancel'} onClick={() => act('cancel', () => bookingApi.cancel(b.trackingId, { reason: cancelReason, detail: cancelDetail })).then(() => setShowCancel(false))} className="mt-5 w-full cursor-pointer rounded-xl border-0 bg-red-700 px-5 py-3 text-sm font-bold text-white disabled:opacity-50">{busy === 'cancel' ? 'Cancelling...' : 'Confirm cancellation'}</button>
                    </section>
                  )}
                </div>
              )}
            </section>

            {b.chat && (
              <aside className="min-w-0">
                <section className={`${cardClass} flex h-[clamp(28rem,65vh,42rem)] min-h-0 flex-col xl:sticky xl:top-24`}>
                  <div className="flex min-w-0 items-start gap-2 border-b border-slate-100 pb-4 text-sm font-black text-slate-950"><MessageCircle size={18} className="mt-0.5 shrink-0 text-green-700" /> <span className="min-w-0 break-words">Chat with {b.gigger?.name}</span></div>
                  <div ref={scrollRef} className="min-h-0 flex-1 space-y-2 overflow-y-auto py-4">
                    {thread.length === 0 ? (
                      <div className="flex h-full items-center justify-center px-3 text-center text-xs text-slate-400">No messages yet. Say hello.</div>
                    ) : (
                      thread.map((message, index) => (
                        <div key={message._id ? `${message._id}` : message.clientMessageId || `tmp-${index}`} className={`flex min-w-0 ${message.from === 'me' ? 'justify-end' : 'justify-start'} ${message.pending ? 'opacity-60' : ''}`}>
                          <div className={`max-w-[84%] break-words rounded-2xl px-3.5 py-2.5 text-sm leading-5 ${message.from === 'me' ? 'rounded-br-md bg-green-700 text-white' : 'rounded-bl-md bg-slate-100 text-slate-800'}`}>{message.text}</div>
                        </div>
                      ))
                    )}
                  </div>
                  <form onSubmit={sendChat} className="flex min-w-0 gap-2 border-t border-slate-100 pt-4">
                    <input className={`${inputClass} min-w-0 flex-1`} value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Type a message..." />
                    <button type="submit" aria-label="Send message" className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-xl border-0 bg-green-700 text-white"><Send size={16} /></button>
                  </form>
                </section>
              </aside>
            )}
          </div>
        </div>

        {showTasks && (
          <AdditionalTasksModal
            booking={b}
            tasks={tasks}
            canAddTasks={canAddTasks}
            giggerName={giggerName}
            clientRate={clientRate}
            onClose={() => setShowTasks(false)}
            onChanged={load}
          />
        )}

        {payModal === 'deposit' && (
          <PaymentModal
            title="Pay your deposit"
            headline={`A 20% deposit of $${depositAmount.toFixed(2)} confirms your booking with ${giggerName}. The balance is due once the job's complete.`}
            fetchIntent={() => bookingApi.createDepositIntent(b.trackingId)}
            onClose={() => setPayModal(null)}
            onPaid={async (intentId) => {
              await bookingApi.confirmDeposit(b.trackingId, intentId);
              setPayModal(null);
              await load();
            }}
          />
        )}
        {payModal === 'balance' && (
          <PaymentModal
            title="Pay your balance"
            headline={`Pay the remaining $${balanceAmount.toFixed(2)} to finish up with ${giggerName}.`}
            fetchIntent={() => bookingApi.createBalanceIntent(b.trackingId)}
            onClose={() => setPayModal(null)}
            onPaid={async (intentId) => {
              await bookingApi.confirmBalance(b.trackingId, intentId);
              setPayModal(null);
              await load();
            }}
          />
        )}
      </main>

      <Footer />
    </>
  );
};

export default Track;
